import { canonicalTimeZone, displayTimeZone, durationSince, formatFull, formatWhen } from './time';

/**
 * A time on screen (TOADO-904): in the person's zone, with the full date,
 * time and zone on hover, and the exact moment for anything that reads it.
 */
export function When({ at, dateOnly, exact, prefix, className }: {
  at: string | number | Date;
  dateOnly?: boolean;
  /** The date and the time, whenever it was: a ticket's times. */
  exact?: boolean;
  /** Words before the time, such as "Opened ". */
  prefix?: string;
  className?: string;
}) {
  const date = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(date.getTime())) return null;
  return (
    <time className={className} dateTime={date.toISOString()} title={formatFull(date)}>
      {prefix}{formatWhen(date, { dateOnly, exact })}
    </time>
  );
}

/**
 * How long something has been so ("Waiting 3 days"): a duration stays one,
 * with the moment it started on hover (TOADO-904).
 */
export function Duration({ since, prefix, className }: { since: string | number | Date; prefix?: string; className?: string }) {
  const date = since instanceof Date ? since : new Date(since);
  if (Number.isNaN(date.getTime())) return null;
  return (
    <time className={className} dateTime={date.toISOString()} title={`Since ${formatFull(date)}`}>
      {prefix}{durationSince(date)}
    </time>
  );
}

/**
 * When a capture was taken, and, when the person who reported it was in
 * another zone, the time it was for them (TOADO-904): "2:41 PM · 7:41 PM for
 * the reporter (Europe/London)". That matters when a client or teammate
 * reports from elsewhere.
 */
export function CapturedAt({ at, reporterZone }: { at: string; reporterZone?: string | null }) {
  // Compared by canonical name: an alias of the viewer's zone is their zone.
  const reporter = canonicalTimeZone(reporterZone);
  const theirs = reporter && reporter !== displayTimeZone() ? formatWhen(at, { zone: reporter, exact: true }) : null;
  return (
    <>
      <When at={at} exact />
      {theirs && (
        <time dateTime={new Date(at).toISOString()} title={formatFull(at, { zone: reporter! })} data-testid="reporter-time">
          {' · '}{theirs} for the reporter ({reporter!.replace(/_/g, ' ')})
        </time>
      )}
    </>
  );
}

/** A card's time: the date and the time, whenever it was (Jason, 22 Oct). */
export const cardTime = (iso: string) => <When at={iso} exact />;

/** A comment's time: the date and the time. */
export const commentTime = (iso: string) => <When at={iso} exact />;
